// path: src/pages/IPA/Negociacoes/pages/NegociacaoFornecedor/components/ExpandedTableContent/competitionSnapshot.ts
import { Competitor } from '../../../../../../../@types/IPA/GerenciadorDePrecos';
import { CompetitionSnapshotState, NegotiationCompetitorsInfoProps } from '../../NegociacaoFornecedor.types';

/**
 * Frase que o card exibe desde sempre quando não há concorrência para mostrar. Continua sendo a
 * resposta certa para a consulta ao mercado de hoje e para uma falha de leitura: nesses casos a
 * tela realmente não sabe dizer o motivo.
 */
export const COMPETITION_INFO_UNAVAILABLE_MESSAGE = 'Informações de concorrência indisponíveis';

const COMPETITION_EMPTY_MESSAGE_BY_STATE: Record<CompetitionSnapshotState, string> = {
  AVAILABLE: COMPETITION_INFO_UNAVAILABLE_MESSAGE,
  NO_COMPETITORS: 'Nenhum concorrente foi encontrado na data do fechamento.',
  UNAVAILABLE: 'Esta negociação foi fechada antes do registro da concorrência.',
};

/**
 * Escolhe a frase de vazio a partir do que o servidor informou sobre a fotografia da concorrência.
 * Um estado ausente ou desconhecido cai na frase de sempre, de modo que uma resposta malformada
 * degrada para o comportamento atual em vez de quebrar a tela.
 */
export const resolveCompetitionEmptyMessage = (state?: CompetitionSnapshotState): string => {
  if (!state) return COMPETITION_INFO_UNAVAILABLE_MESSAGE;

  return COMPETITION_EMPTY_MESSAGE_BY_STATE[state] ?? COMPETITION_INFO_UNAVAILABLE_MESSAGE;
};

/**
 * Diz se há lista de lojas concorrentes para desenhar. A resposta é um booleano de propósito: o
 * tamanho da lista usado direto como condição de renderização faz o zero de uma lista vazia virar
 * texto visível na tela, e com os estados de fotografia a lista vazia deixou de ser exceção.
 */
export const hasCompetitorStoreList = (competitorsInfo?: NegotiationCompetitorsInfoProps): boolean => Boolean(competitorsInfo?.competitorPriceDTO?.length);

type CompetitorSummaryWithPrices = Competitor & Record<'competitorAverage' | 'competitorHigher' | 'competitorLower', number>;

/**
 * Diz se o resumo tem preço para mostrar. A fotografia do fechamento pode ter gravado as lojas sem
 * ter gravado o resumo, e nesse caso as caixas exibiriam zero como se fosse o preço médio da
 * concorrência. A contagem de lojas fica de fora da verificação porque a tela já sabe mostrá-la
 * ausente como zero, e ela sozinha não pode derrubar um resumo que tem preço.
 */
export const hasCompetitorSummary = (summary?: Competitor | null): summary is CompetitorSummaryWithPrices =>
  !!summary && summary.competitorAverage !== null && summary.competitorLower !== null && summary.competitorHigher !== null;
